/* eslint-disable no-console */
import { useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { gql, useMutation } from "@apollo/client";
import { useLocation, useHistory } from "react-router";
import { Helmet } from "react-helmet";
import { addDays } from "date-fns";
import qs from "query-string";
import validator from "validator";
import Layout from "../../components/Layout";
import { useStoreContext } from "../../contexts/StoreProvider";
import TextInput from "../../components/TextInput";
import Button from "../../components/Button";
import LayoutCard from "../../components/LayoutCard";
import { Heading, Text } from "../../components/Type";
import TextLink from "../../components/TextLinks";
import { Info, ArrowRight } from "../../components/Icons/general-interface-1";
import styles from "./index.module.scss";
import {
  sanitizeUser,
  ResponseUserType,
} from "../../contexts/AuthProvider/utils";
import Checkbox from "../../components/Checkbox";

const LOGIN = gql`
  mutation Login($input: UsersPermissionsLoginInput!) {
    login(input: $input) {
      jwt
      user {
        id
        email
        first_name
        last_name
        adp_auth_num
        phone
        role {
          id
          name
          description
          type
        }
        password_updated_at
        mfa_enabled
      }
    }
  }
`;

const SEND_VERIFICATION_CODE = gql`
  mutation SendVerificationCode($input: VerificationCodeInput) {
    sendVerificationCode(input: $input) {
      id
      message
      user {
        id
        email
        mfa_enabled
      }
    }
  }
`;

const CHECK_VERIFICATION_CODE = gql`
  mutation CheckVerificationCode($input: VerificationCodeInput) {
    checkVerificationCode(input: $input) {
      id
      message
      user {
        id
        email
        first_name
        last_name
        adp_auth_num
        phone
        role {
          id
          name
          description
          type
        }
        password_updated_at
        mfa_enabled
      }
    }
  }
`;

const SKIP_VERIFICATION_CODE = gql`
  mutation SkipVerificationCode($input: VerificationCodeInput) {
    skipVerificationCode(input: $input) {
      id
      message
      user {
        id
        email
        first_name
        last_name
        adp_auth_num
        phone
        role {
          id
          name
          description
          type
        }
        password_updated_at
        mfa_enabled
      }
    }
  }
`;

const Login = () => {
  const { dispatch } = useStoreContext();
  const methods = useForm<{ identifier: string; password: string }>({
    mode: "onTouched",
    defaultValues: {
      identifier: "",
      password: "",
    },
  });
  const location = useLocation();
  const history = useHistory();
  const { email } = qs.parse(location.search);

  const [user, setUser] = useState<ResponseUserType | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [isEnabled, setEnabled] = useState(false);
  const [isLogged, setLogged] = useState(false);
  const [expired, setExpired] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const formCode = useRef<HTMLFormElement>(null);
  const code1 = useRef<HTMLInputElement | null>(null);
  const code2 = useRef<HTMLInputElement | null>(null);
  const code3 = useRef<HTMLInputElement | null>(null);
  const code4 = useRef<HTMLInputElement | null>(null);
  const code5 = useRef<HTMLInputElement | null>(null);
  const code6 = useRef<HTMLInputElement | null>(null);

  // authentication mutation
  const [authenticate, { loading: loadingLogin }] = useMutation(LOGIN, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      setUser(data.login.user);
      setLogged(true);
      setError(undefined);

      if (data.login.jwt === "authenticated") {
        const days = process.env.REACT_APP_PASSWORD_EXPIRED_DAYS
          ? Number(process.env.REACT_APP_PASSWORD_EXPIRED_DAYS)
          : 90;
        if (
          !data.login.user.password_updated_at ||
          new Date() >
            addDays(new Date(data.login.user.password_updated_at), days)
        ) {
          setExpired(true);
        } else {
          dispatch({
            type: "UPDATE_USER",
            payload: sanitizeUser(data.login.user),
          });
        }
      } else {
        setEnabled(data.login.user.mfa_enabled);
      }
      return null;
    },
    onError: (err) => {
      setError(
        err.graphQLErrors?.[0]?.extensions?.exception?.data?.data?.[0]
          ?.messages?.[0]?.message ||
          err.graphQLErrors?.[0]?.message ||
          err.message ||
          "An unknown error has occurred"
      );
      return null;
    },
  });

  // check verification mutation
  const [checkVerificationCode, { loading: loadingCheck }] = useMutation(
    CHECK_VERIFICATION_CODE,
    {
      fetchPolicy: "no-cache",
      onCompleted: (data) => {
        if (data.checkVerificationCode.id === "auth.ok") {
          setUser(data.checkVerificationCode.user);
          const days = process.env.REACT_APP_PASSWORD_EXPIRED_DAYS
            ? Number(process.env.REACT_APP_PASSWORD_EXPIRED_DAYS)
            : 90;
          if (
            !data.checkVerificationCode.user.password_updated_at ||
            new Date() >
              addDays(
                new Date(data.checkVerificationCode.user.password_updated_at),
                days
              )
          ) {
            setExpired(true);
          } else {
            dispatch({
              type: "UPDATE_USER",
              payload: sanitizeUser(data.checkVerificationCode.user),
            });
          }
        } else {
          if (data.checkVerificationCode.id === "auth.max.attempt") {
            setDisabled(true);
          } else {
            setError(data.checkVerificationCode.message);
          }
        }
        return null;
      },
      onError: (err) => {
        setError(
          err.graphQLErrors?.[0]?.extensions?.exception?.data?.data?.[0]
            ?.messages?.[0]?.message ||
            err.graphQLErrors?.[0]?.message ||
            err.message ||
            "An unknown error has occurred"
        );
        return null;
      },
    }
  );

  // send verification mutation
  const [sendVerificationCode, { loading: loadingSend }] = useMutation(
    SEND_VERIFICATION_CODE,
    {
      fetchPolicy: "no-cache",
      onCompleted: (data) => {
        if (data.sendVerificationCode.id === "auth.ok") {
          setUser(data.sendVerificationCode.user);
          setDisabled(false);
          setError(undefined);
          setEnabled(true);
        } else {
          setError(data.sendVerificationCode.message);
        }
        return null;
      },
      onError: (err) => {
        setError(
          err.graphQLErrors?.[0]?.extensions?.exception?.data?.data?.[0]
            ?.messages?.[0]?.message ||
            err.graphQLErrors?.[0]?.message ||
            err.message ||
            "An unknown error has occurred"
        );
        return null;
      },
    }
  );

  // skip verification mutation
  const [skipVerificationCode, { loading: loadingSkip }] = useMutation(
    SKIP_VERIFICATION_CODE,
    {
      fetchPolicy: "no-cache",
      onCompleted: (data) => {
        if (data.skipVerificationCode.id === "auth.ok") {
          setUser(data.skipVerificationCode.user);
          const days = process.env.REACT_APP_PASSWORD_EXPIRED_DAYS
            ? Number(process.env.REACT_APP_PASSWORD_EXPIRED_DAYS)
            : 90;
          if (
            !data.skipVerificationCode.user.password_updated_at ||
            new Date() >
              addDays(
                new Date(data.skipVerificationCode.user.password_updated_at),
                days
              )
          ) {
            setExpired(true);
          } else {
            dispatch({
              type: "UPDATE_USER",
              payload: sanitizeUser(data.skipVerificationCode.user),
            });
          }
        } else {
          setError(data.skipVerificationCode.message);
        }
        return null;
      },
      onError: (err) => {
        setError(
          err.graphQLErrors?.[0]?.extensions?.exception?.data?.data?.[0]
            ?.messages?.[0]?.message ||
            err.graphQLErrors?.[0]?.message ||
            err.message ||
            "An unknown error has occurred"
        );
        return null;
      },
    }
  );

  // ======================
  // VERIFICATION CODE FLOW
  // ======================

  // user choose to skip verification code, just authenticate
  const onSkipVerification = async () => {
    await skipVerificationCode({
      variables: {
        input: {
          userId: user?.id,
        },
      },
    });
  };

  // user choose to enable verification code,
  const onConfirmVerification = async () => {
    await sendVerificationCode({
      variables: {
        input: {
          userId: user?.id,
        },
      },
    });
  };

  if (isLogged && !isEnabled && !expired) {
    return (
      <Layout>
        <Helmet>
          <title>Motion EASI Login</title>
        </Helmet>
        <LayoutCard
          header={
            <Heading size="h4">Help us further secure Motion EASI.</Heading>
          }
        >
          <div>
            <Text>
              Set up 2 step authentication by verifying your email. Each time
              you log in, you&quot;ll need your password and a 6-digit
              verification code sent to your email.
            </Text>
            <br />
            <Text>Email</Text>
            <Text>{user?.email}</Text>

            <div className={styles.codeButtonRow}>
              <Button
                secondary
                onClick={onSkipVerification}
                disabled={loadingSkip}
              >
                Skip Authentication
              </Button>
              <Button
                primary
                icon={<ArrowRight />}
                onClick={onConfirmVerification}
                disabled={loadingSend}
              >
                Confirm Email
              </Button>
            </div>
          </div>
        </LayoutCard>
      </Layout>
    );
  }

  // generate a new verification code
  const onResendCode = async () => {
    setError(undefined);
    formCode?.current?.reset();
    await sendVerificationCode({
      variables: {
        input: {
          userId: user?.id,
        },
      },
    });
  };

  // check if code matches
  const onVerifyCode = async (values) => {
    setError(undefined);
    const verificationCode = `${code1?.current?.value}${code2?.current?.value}${code3?.current?.value}${code4?.current?.value}${code5?.current?.value}${code6?.current?.value}`;
    await checkVerificationCode({
      variables: {
        input: {
          userId: user?.id,
          remember: values["remember-device"] === "mb-yes",
          code: verificationCode,
        },
      },
    });
  };

  const onChangeCode = (event) => {
    const fieldIndex = event.target.name.split("-");
    if (parseInt(fieldIndex[1], 10) < 6) {
      const nextSibling = document.querySelector(
        `input[name=code-${parseInt(fieldIndex[1], 10) + 1}]`
      );
      if (nextSibling !== null) {
        (nextSibling as HTMLElement).focus();
        (nextSibling as HTMLInputElement).select();
      }
    }
  };

  const onPasteCode = (event) => {
    const paste = event.clipboardData.getData("Text");
    if (paste) {
      (document.querySelector(`input[name=code-1]`) as HTMLInputElement).value =
        paste[0] ? paste[0] : "";
      (document.querySelector(`input[name=code-2]`) as HTMLInputElement).value =
        paste[1] ? paste[1] : "";
      (document.querySelector(`input[name=code-3]`) as HTMLInputElement).value =
        paste[2] ? paste[2] : "";
      (document.querySelector(`input[name=code-4]`) as HTMLInputElement).value =
        paste[4] ? paste[4] : "";
      (document.querySelector(`input[name=code-5]`) as HTMLInputElement).value =
        paste[5] ? paste[5] : "";
      (document.querySelector(`input[name=code-6]`) as HTMLInputElement).value =
        paste[6] ? paste[6] : "";
    }
  };

  if (isLogged && isEnabled && !expired) {
    return (
      <Layout>
        <Helmet>
          <title>Motion EASI Login</title>
        </Helmet>
        <LayoutCard
          header={
            <Heading size="h4">
              Please check your email for a 6-digit code
            </Heading>
          }
        >
          <FormProvider {...methods}>
            <form ref={formCode} onPaste={onPasteCode}>
              <div>
                <Text>
                  Enter the code we sent to your email to continue the
                  validation process.
                </Text>
                <div>
                  <div className={styles.codeButtonRow}>
                    <input
                      type="text"
                      ref={code1}
                      name="code-1"
                      maxLength={1}
                      className={styles.codeInput}
                      onChange={onChangeCode}
                      disabled={loadingCheck}
                    />
                    <input
                      type="text"
                      ref={code2}
                      name="code-2"
                      maxLength={1}
                      className={styles.codeInput}
                      onChange={onChangeCode}
                      disabled={loadingCheck}
                    />
                    <input
                      type="text"
                      ref={code3}
                      name="code-3"
                      maxLength={1}
                      className={styles.codeInput}
                      onChange={onChangeCode}
                      disabled={loadingCheck}
                    />
                    <input
                      type="text"
                      ref={code4}
                      name="code-4"
                      maxLength={1}
                      className={styles.codeInput}
                      onChange={onChangeCode}
                      disabled={loadingCheck}
                    />
                    <input
                      type="text"
                      ref={code5}
                      name="code-5"
                      maxLength={1}
                      className={styles.codeInput}
                      onChange={onChangeCode}
                      disabled={loadingCheck}
                    />
                    <input
                      type="text"
                      ref={code6}
                      name="code-6"
                      maxLength={1}
                      className={styles.codeInput}
                      onChange={onChangeCode}
                      disabled={loadingCheck}
                    />
                  </div>
                  {error ? (
                    <div className={styles.codeError}>
                      <Info />
                      {error}
                    </div>
                  ) : null}
                </div>
                <div className={styles.codeContainer}>
                  <Checkbox
                    name="remember-device"
                    value="mb-yes"
                    label={`Remember device for ${process.env.REACT_APP_REMEMBER_DEVICE_DAYS} days`}
                    disabled={loadingCheck || loadingSend}
                  />
                </div>
                <div className={styles.codeButtonRow}>
                  <Button
                    secondary
                    onClick={methods.handleSubmit(onResendCode)}
                    disabled={loadingSend || loadingCheck}
                  >
                    Resend code
                  </Button>
                  <Button
                    primary
                    icon={<ArrowRight />}
                    onClick={methods.handleSubmit(onVerifyCode)}
                    disabled={loadingCheck || disabled || loadingSend}
                  >
                    Done
                  </Button>
                </div>
              </div>
            </form>
          </FormProvider>
        </LayoutCard>
      </Layout>
    );
  }

  // =====================
  // PASSWORD EXPIRED FLOW
  // =====================
  const onPasswordExpired = (user) => {
    dispatch({
      type: "UPDATE_USER",
      payload: sanitizeUser(user),
    });
    history.replace("/change-password");
    return null;
  };

  if (expired) {
    return (
      <Layout>
        <Helmet>
          <title>Motion EASI Login</title>
        </Helmet>
        <LayoutCard
          header={<Heading size="h4">Your password has expired.</Heading>}
        >
          <div>
            <Text>Please change your password at this time.</Text>
            <div className={styles.changeButtonRow}>
              <Button
                primary
                icon={<ArrowRight />}
                onClick={() => onPasswordExpired(user)}
              >
                Change password
              </Button>
            </div>
          </div>
        </LayoutCard>
      </Layout>
    );
  }

  // ==========
  // LOGIN FLOW
  // ==========
  const onLogin = async (values: { identifier: string; password: string }) => {
    // check if username and password
    await authenticate({
      variables: {
        input: {
          identifier: values.identifier,
          password: values.password,
          provider: "local",
        },
      },
    });
  };

  return (
    <Layout>
      <Helmet>
        <title>Motion EASI Login</title>
      </Helmet>
      <LayoutCard
        error={error}
        header={<Heading size="h4">Login to EASI</Heading>}
        footer={
          <Text size="small">
            Don’t have an account?{" "}
            <a
              href={`mailto:${process.env.REACT_APP_MOTION_SUPPORT_EMAIL}`}
              target="_blank"
              rel="noreferrer"
            >
              Contact Motion
            </a>{" "}
            to set one up.
          </Text>
        }
      >
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onLogin)}>
            <TextInput
              id="identifier"
              label="Email"
              placeholder=""
              defaultValue={email || undefined}
              fieldOptions={{
                required: "Enter your email",
                validate: {
                  isEmail: (value) =>
                    !validator.isEmail(value as string)
                      ? "This is not a valid email"
                      : undefined,
                },
              }}
            />
            <TextInput
              id="password"
              label="Password"
              placeholder=""
              type="password"
              showEyeIcon
              fieldOptions={{ required: "Enter a password" }}
            />
            <div className={styles.loginButtonRow}>
              <TextLink to="/forgot-password">Forgot Password?</TextLink>
              <Button
                primary
                submit
                icon={<ArrowRight />}
                className={styles.button}
                disabled={loadingLogin}
              >
                Login
              </Button>
            </div>
          </form>
        </FormProvider>
      </LayoutCard>
    </Layout>
  );
};

export default Login;
